/* @flow */
import * as React from 'react';
import { values } from 'lodash';

import Row from 'components/Row.jsx';
import View from 'components/View.jsx';
import Views from 'components/Views.jsx';
import type { ListActionsFilterState } from '../ListActions/ListActions.jsx';
import PeopleListSidebar from './PeopleListSidebar.jsx';
import type { SidebarData } from './PeopleListSidebar.jsx';
import PeopleListTableDataContainer from './PeopleListTableDataContainer.jsx';
import type { PeopleListRow } from './PeopleListTableDataContainer.jsx';
import './styles/PeopleList.scss';
import type { PeopleListFilterState } from './PeopleListFilterUIDataContainer.jsx';
import PeopleListFilterUIDataContainer from './PeopleListFilterUIDataContainer.jsx';
import type { SubscribeInfoType } from 'entities/Group';

export type EntityType = 'organization' | 'group';
export type UserRole = 'teacher' | 'student' | 'parent' | 'admin' | 'unknown' | 'all';

export type GroupInfo = {
  id: number,
  name: ?string,
  className: string,
  staffClassOrgId: ?number,
  hasChildren: boolean,
  pdfUrl: string,
  joinUrl: string,
  groupAvatar: ?{ svgUrl: ?string },
  subscribe: SubscribeInfoType,
};

type Props = {
  entityType: EntityType,
  entityUuid: string,
  groupInfo: GroupInfo,
  associatedOrg: ?Object,
};

type State = {
  query: ?string,
  filters: PeopleListFilterState,
  sidebarOpen: boolean,
  selectedUserId: ?number,
  batchUsers: { [number]: PeopleListRow },
  reloadPeopleListQuery: boolean,
};

export default class PeopleList extends React.Component<Props, State> {
  state: State = {
    filters: {
      role: 'teacher',
    },
    query: null,
    sidebarOpen: false,
    selectedUserId: null,
    batchUsers: {},
    reloadPeopleListQuery: false,
  };

  shouldComponentUpdate(nextProps: Object, nextState: Object) {
    const { reloadPeopleListQuery } = this.state;
    return !(reloadPeopleListQuery && !nextState.reloadPeopleListQuery);
  }

  handleFiltersChange = (activeFilters: ListActionsFilterState) => {
    this.setState({ filters: (activeFilters: any) });
  };

  handleQueryChange = (query: string) => {
    this.setState({ query });
  };

  handlePersonClicked = (row: Object) => {
    const {
      user: { id },
    } = row;

    this.setState({
      sidebarOpen: true,
      selectedUserId: id,
    });
  };

  handleSidebarClose = () => {
    const { batchUsers } = this.state;
    this.setState({
      sidebarOpen: Object.keys(batchUsers).length > 0,
      selectedUserId: null,
    });
  };

  deselectAll = () => {
    this.setState({
      sidebarOpen: false,
      batchUsers: {},
    });
  };

  addUserToSelection = (row: PeopleListRow) => {
    const { batchUsers } = this.state;
    this.setState({
      batchUsers: Object.assign({}, batchUsers, {
        [row.user.id]: row,
      }),
      sidebarOpen: true,
      selectedUserId: null,
    });
  };

  removeUserFromSelection = (row: PeopleListRow) => {
    const { batchUsers } = this.state;
    const newUsers = Object.assign({}, batchUsers);
    delete newUsers[row.user.id];
    this.setState({
      batchUsers: newUsers,
      sidebarOpen: Object.keys(newUsers).length > 0,
      selectedUserId: null,
    });
  };

  reloadList = () => {
    this.setState(
      {
        reloadPeopleListQuery: true,
      },
      () =>
        this.setState({
          reloadPeopleListQuery: false,
        })
    );
  };

  render() {
    const { entityType, entityUuid, groupInfo, associatedOrg } = this.props;
    const {
      filters,
      query,
      sidebarOpen,
      selectedUserId,
      batchUsers,
      reloadPeopleListQuery,
    } = this.state;

    const sidebarProps = ((): SidebarData => {
      if (selectedUserId) {
        return {
          type: 'user-profile',
          data: {
            userId: selectedUserId,
            organizationUuid: entityUuid,
          },
        };
      } else {
        return {
          type: 'batch',
          data: {
            users: values(batchUsers),
            onDeselectAll: this.deselectAll,
            associatedOrg,
            reloadList: this.reloadList,
          },
        };
      }
    })();

    const { source, grade, role } = filters;

    const rosterFilterType = (() => {
      if (source && source.length === 1) {
        return source[0];
      } else {
        return 'all';
      }
    })();

    return (
      <Views className="PeopleList">
        <View className="PeopleList-table" gutter="small" scroll>
          <Row>
            <PeopleListFilterUIDataContainer
              entityUuid={entityUuid}
              filters={filters}
              onFiltersChange={this.handleFiltersChange}
              onQueryChange={this.handleQueryChange}
              reloadQuery={reloadPeopleListQuery}
            />
          </Row>
          <PeopleListTableDataContainer
            ref="table"
            entityType={entityType}
            entityUuid={entityUuid}
            groupInfo={groupInfo}
            query={query}
            grade={(grade && grade.enabled && grade.value) || null}
            role={role}
            rostered={rosterFilterType}
            onPersonClicked={this.handlePersonClicked}
            selectedUserIds={Object.keys(batchUsers).map(k => parseInt(k, 10))}
            reloadQuery={reloadPeopleListQuery}
            onUserAddedToSelection={this.addUserToSelection}
            onUserRemovedFromSelection={this.removeUserFromSelection}
          />
        </View>
        {sidebarOpen && (
          <View className="PeopleList-sidebar" full scroll>
            <PeopleListSidebar sidebarProps={sidebarProps} onClose={this.handleSidebarClose} />
          </View>
        )}
      </Views>
    );
  }
}
